// @ts-nocheck
import './styles.scss';
import { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { Select as MuiSelect, MenuItem } from '@mui/material';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import AccordionMenu from '../../../common/accordion-menu';
import TooltipIcon from '../../../common/tooltip-icon';
import Loading from '../../../common/loading';
import { ReactComponent as InfoIcon } from '../../../../assets/icons/info-unfilled.svg';
import { countriesByRegions } from '../../../../config';
import l from '../../../../lang';
import { getCountryFlagUrl } from '../../../../utils';
import { canModifyPaymentMethods } from '../../../../store/payment-methods/roles';
import { useInstallmentsInfo } from '../../../../hooks/use-installments-info';
import './payer-select.scss';

const REGIONS = [
  { id: 'LA', titleKey: 'region.LA' },
  { id: 'AF_ME', titleKey: 'region.AF_ME' },
  { id: 'AS', titleKey: 'region.AS' },
];

const countryTooltipMap = {
  AR: 'paymentMethod.installments.tooltip.argentina',
  UY: 'paymentMethod.installments.tooltip.uruguay',
};

const payerValue = row => (row.paymentResponsible === 'merchant' ? 'MERCHANT' : 'END_USER');

const PayerSelect = ({ value, onChange, disabled }) => (
  <MuiSelect
    value={value}
    onChange={e => onChange(e.target.value)}
    disabled={disabled}
    className="payer-select"
    IconComponent={ArrowDropDownIcon}
  >
    <MenuItem value="INACTIVE">{l('paymentMethod.installments.inactive')}</MenuItem>
    <MenuItem value="END_USER">{l('paymentMethod.installments.buyer')}</MenuItem>
    <MenuItem value="MERCHANT">{l('paymentMethod.installments.merchant')}</MenuItem>
  </MuiSelect>
);

const InstallmentsTable = ({ rows, canModify, payerByCountry, onPayerChange }) => (
  <div className="installments__table-panel">
    <table className="installments__table">
      <thead>
        <tr>
          <th>{l('paymentMethod.installments.header.country')}</th>
          <th>{l('paymentMethod.installments.header.available')}</th>
          <th>
            <span className="installments__header-with-icon">
              {l('paymentMethod.installments.header.paymentResponsible')}
              <TooltipIcon
                reducedPadding
                placement="bottom"
                text={l('paymentMethod.installments.tooltip.paymentResponsible')}
                icon={<InfoIcon className="installments__info-icon" aria-hidden />}
              />
            </span>
          </th>
          <th>{l('paymentMethod.installments.header.cost')}</th>
        </tr>
      </thead>
      <tbody>
        {rows.map(row => {
          const currentPayer = payerByCountry[row.countryCode] || payerValue(row);
          const selectValue = row.enabled ? currentPayer : 'INACTIVE';
          return (
            <tr key={row.countryCode}>
              <td>
                <div className="installments__country">
                  <img alt="" className="installments__country-flag" src={getCountryFlagUrl(row.countryCode)} />
                  <span>{l(`countryCode.${row.countryCode}`)}</span>
                  {row.hasNote && <span>*</span>}
                </div>
              </td>
              <td>
                <span className="installments__available">
                  {row.available ? l('common.yes') : l('common.no')}
                  {row.available && countryTooltipMap[row.countryCode] && (
                    <TooltipIcon
                      reducedPadding
                      placement="bottom"
                      text={l(countryTooltipMap[row.countryCode])}
                      icon={<InfoIcon className="installments__info-icon" aria-hidden />}
                    />
                  )}
                </span>
              </td>
              <td>
                {!row.available ? (
                  <span className="installments__not-applicable">{l('paymentMethod.installments.noApply')}</span>
                ) : row.payerSelectable ? (
                  <PayerSelect
                    value={selectValue}
                    disabled={!canModify || !row.isInstallmentsAllowed}
                    onChange={value => onPayerChange(row, value)}
                  />
                ) : (
                  <span className="installments__payer-locked">
                    {currentPayer === 'MERCHANT'
                      ? l('paymentMethod.installments.merchant')
                      : l('paymentMethod.installments.buyer')}
                  </span>
                )}
              </td>
              <td>
                {!row.available ? (
                  l('paymentMethod.installments.noApply')
                ) : row.cost === 'issuer' ? (
                  l('paymentMethod.installments.issuerCost')
                ) : (
                  <a
                    className="installments__cost-link"
                    href="https://40013370.fs1.hubspotusercontent-na1.net/hubfs/40013370/Pricing/dLocal%20Go_Installments.pdf"
                    target="_blank"
                    rel="noreferrer"
                  >
                    {l('paymentMethod.installments.merchantCost')}
                  </a>
                )}
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  </div>
);

function Installments() {
  const { installmentsCountryInfos, disableInstallmentsCountry, enableInstallmentsCountry, loading } =
    useInstallmentsInfo();
  const { auth } = useSelector(state => state);
  const canModify = canModifyPaymentMethods(auth?.roles);
  const [payerByCountry, setPayerByCountry] = useState({});

  const rowsByRegion = useMemo(() => {
    const assigned = new Set();
    const grouped = {};
    REGIONS.forEach(region => {
      const codes = countriesByRegions[region.id] || [];
      grouped[region.id] = codes
        .map(code => installmentsCountryInfos.find(row => row.countryCode === code))
        .filter(Boolean);
      codes.forEach(code => assigned.add(code));
    });
    const extras = installmentsCountryInfos.filter(row => !assigned.has(row.countryCode));
    if (extras.length) {
      grouped.LA = [...(grouped.LA || []), ...extras];
    }
    return grouped;
  }, [installmentsCountryInfos]);

  const handlePayerChange = (row, value) => {
    if (value === 'INACTIVE') {
      const payer = payerByCountry[row.countryCode] || payerValue(row);
      disableInstallmentsCountry(row.countryCode, payer);
      return;
    }
    setPayerByCountry(prev => ({ ...prev, [row.countryCode]: value }));
    enableInstallmentsCountry(row.countryCode, value);
  };

  const items = REGIONS.map(region => {
    const rows = rowsByRegion[region.id] || [];
    return {
      id: region.id,
      title: l(region.titleKey),
      disabled: rows.length === 0,
      content: rows.length ? (
        <InstallmentsTable
          rows={rows}
          canModify={canModify}
          payerByCountry={payerByCountry}
          onPayerChange={handlePayerChange}
        />
      ) : null,
    };
  });

  return (
    <div className="installments">
      <div className="installments__title">{l('paymentMethod.installments.byCountry')}</div>
      {loading && (
        <div className="installments__loading">
          <Loading />
        </div>
      )}
      {!loading && (
        <div className="installments__content">
          <AccordionMenu items={items} />
        </div>
      )}
      {installmentsCountryInfos.some(row => row.hasNote) && (
        <span className="installments-note">{l('paymentMethod.installments.argentinaNote')}</span>
      )}
    </div>
  );
}

export default Installments;
